/**
 * Global AI Chat Panel — the library/dashboard tab manifest for RightPanelShell.
 *
 * Tabs: Search (keyword + RAG behind one input) | Universe, plus the dashboard's
 * Conversation and Notifications. Its docked/floating behavior follows the same
 * responsive contract as the reader panel; the panel chrome lives in the shell.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from '@legendapp/state/react';
import { Bell, Brain, MessagesSquare, Search } from 'lucide-react';
import { ui$, closeRAGPanel } from '../store/ui$';
import { auth$, getListArticleCounts, getListById } from '../store';
import { isMobile } from '../constants/breakpoints';
import { useRightPanelIsPage } from '../hooks/useRightPanelLayout';
import { AIHeaderAction } from './AIHeaderAction';
import MobileAIPage from './mobile/MobileAIPage';
import MobileAskPill from './mobile/MobileAskPill';
import RightPanelShell from './RightPanelShell';
import FeatureBoundary from './FeatureBoundary';
import LibrarySearchTab from './ai-chat/LibrarySearchTab';
import UniverseView from './UniverseView';
import LastChatPreview from './dashboard/LastChatPreview';
import DashboardNotifications from './dashboard/DashboardNotifications';
import './AIChatPanel.css';

/**
 * The page's subject line: what the conversation is about, in the words the
 * screen behind it uses. Asked from a list, the AI answers across that list.
 */
const getListSubject = ({ isDashboard, routeListId, pathname, t }) => {
  const counts = getListArticleCounts();

  if (isDashboard) {
    return `${t('sidebar:all')} · ${t('articles:mobile.articleCount', { count: counts.all || 0 })}`;
  }
  if (routeListId) {
    const list = getListById(routeListId);
    return `${list?.name ?? t('common:loading')} · ${t('articles:mobile.articleCount', { count: counts[routeListId] || 0 })}`;
  }
  const isAll = pathname === '/all';
  return `${t(isAll ? 'sidebar:all' : 'sidebar:inbox')} · ${t('articles:mobile.articleCount', { count: (isAll ? counts.all : counts.inbox) || 0 })}`;
};

const GlobalAIChatPanel = ({ onClose, listId }) => {
  const { t } = useTranslation(['reader', 'articles', 'settings', 'sidebar', 'common']);
  const location = useLocation();
  const routeListId = useParams().listId;
  const isDashboard = location.pathname === '/dashboard';
  const isPage = useRightPanelIsPage();

  const [activeTab, setActiveTab] = useState('search');
  const [unreadCount, setUnreadCount] = useState(0);

  const panelHidden = useSelector(() => ui$.ragPanelHidden?.get() ?? true);
  const userId = useSelector(() => auth$.user.get()?.id);
  const pageSubject = useSelector(() => getListSubject({
    isDashboard,
    routeListId,
    pathname: location.pathname,
    t,
  }));

  // A persisted open state would cover the list itself on mobile, where the panel
  // is an overlay rather than a dock — start closed and let the user open it.
  useEffect(() => {
    if (isMobile()) closeRAGPanel();
  }, []);

  const tabs = [
    { id: 'search', label: t('reader:ragChat.tabLabel'), icon: Search },
    // Universe is scoped to a list, so it belongs to list views only — the
    // dashboard trades it for its own hub tabs.
    ...(isDashboard ? [
      { id: 'conversation', label: t('articles:dashboard.lastChat'), icon: MessagesSquare },
      {
        id: 'notifications',
        label: t('settings:sections.notifications'),
        icon: Bell,
        badge: unreadCount > 0
          ? <span className="dashboard-panel-unread-dot" aria-hidden="true" />
          : null,
      },
    ] : [
      { id: 'universe', label: t('articles:tabs.universe'), icon: Brain },
    ]),
  ];

  // The tab set differs between the dashboard and list views, so a tab selected on
  // one route can be absent after navigating to the other — fall back to Search
  // rather than leaving the panel on a tab that no longer exists.
  const currentTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : 'search';

  const handleClose = onClose || closeRAGPanel;

  if (isPage) {
    return (
      <MobileAIPage
        isOpen={!panelHidden}
        onClose={handleClose}
        eyebrow={t('reader:mobile.askingAbout')}
        subject={pageSubject}
        closeLabel={t('reader:aiChat.hideAIChat')}
        className="mobile-ai-page--library"
      >
        <FeatureBoundary>
          <LibrarySearchTab onDismiss={handleClose} />
        </FeatureBoundary>
      </MobileAIPage>
    );
  }

  return (
    <RightPanelShell
      panelId="global-ai-panel"
      className={`global-rag-panel${isDashboard ? ' dashboard-hub-panel' : ''}`}
      isOpen={!panelHidden}
      onClose={handleClose}
      tabs={tabs}
      activeTab={currentTab}
      onTabChange={setActiveTab}
      closeLabel={t('reader:aiChat.hideAIChat')}
    >
      {currentTab === 'search' && (
        <FeatureBoundary>
          <LibrarySearchTab onDismiss={handleClose} />
        </FeatureBoundary>
      )}

      {currentTab === 'universe' && userId && (
        <FeatureBoundary>
          <UniverseView userId={userId} listId={listId ?? 'all'} />
        </FeatureBoundary>
      )}

      {/* The hub tabs are plain scrolling documents rather than self-managing
          layouts like Search, so they keep the padded scroll container. */}
      {currentTab === 'conversation' && (
        <FeatureBoundary>
          <div className="dashboard-panel-content">
            <LastChatPreview />
          </div>
        </FeatureBoundary>
      )}

      {currentTab === 'notifications' && (
        <FeatureBoundary>
          <div className="dashboard-panel-content">
            <DashboardNotifications onUnreadCountChange={setUnreadCount} />
          </div>
        </FeatureBoundary>
      )}
    </RightPanelShell>
  );
};

/**
 * Opener for the library panel. Lives in the app-shell header slot, so it stays put
 * while the panel it controls animates.
 */
export const GlobalAIChatToggle = ({ onOpen }) => {
  const { t } = useTranslation('reader');
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  const panelHidden = useSelector(() => ui$.ragPanelHidden?.get() ?? true);
  const isPage = useRightPanelIsPage();

  if (!panelHidden) return null;

  // The phone shell has no app header to host this, and 1a's rule puts every
  // repeated action in the thumb zone — so Ask takes the same corner and the
  // same shape it has in the reader dock.
  if (isPage) {
    return (
      <MobileAskPill
        floating
        onClick={onOpen}
        expanded={false}
        label={t('ragChat.openAISearch')}
        data-testid="mobile-library-ask"
      />
    );
  }

  return (
    <AIHeaderAction
      className={`ai-chat-toggle global-rag-toggle${isDashboard ? ' dashboard-hub-toggle' : ''}`}
      onClick={onOpen}
      label={t('ragChat.openAISearch')}
      panelId="global-ai-panel"
    />
  );
};

export default GlobalAIChatPanel;
